import { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import {
  Paperclip, Upload, Loader2, Trash2, Download, File, Image,
  FileText, FileSpreadsheet, Film
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILE_ICONS = {
  pdf: FileText, doc: FileText, docx: FileText, txt: FileText,
  xls: FileSpreadsheet, xlsx: FileSpreadsheet, csv: FileSpreadsheet,
  png: Image, jpg: Image, jpeg: Image, gif: Image, webp: Image,
  mp4: Film, mp3: Film,
};

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function FileAttachments({ entityType, entityId }) {
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (entityId) fetchAttachments();
  }, [entityId]);

  const fetchAttachments = async () => {
    try {
      const res = await fetch(`${API}/attachments?entity_type=${entityType}&entity_id=${entityId}`, { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setAttachments(d.attachments || []);
      }
    } catch (e) { /* silent */ }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max file size is 10MB"); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const uploadRes = await fetch(`${API}/attachments/upload`, {
        method: "POST", credentials: "include", body: formData
      });

      if (!uploadRes.ok) {
        const err = await uploadRes.json();
        toast.error(err.detail || "Upload failed");
        return;
      }

      const uploaded = await uploadRes.json();

      // Link to entity
      await fetch(`${API}/attachments/${uploaded.file_id}/link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ entity_type: entityType, entity_id: entityId })
      });

      toast.success(`Uploaded ${file.name}`);
      fetchAttachments();
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async (fileId) => {
    try {
      await fetch(`${API}/attachments/${fileId}`, { method: "DELETE", credentials: "include" });
      fetchAttachments();
      toast.success("File deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  const handleDownload = (fileId, name) => {
    window.open(`${API}/attachments/${fileId}/download`, "_blank");
  };

  return (
    <div className="space-y-2" data-testid="file-attachments">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-slate-600 flex items-center gap-1.5">
          <Paperclip className="w-3.5 h-3.5" />
          Attachments {attachments.length > 0 && <Badge variant="secondary" className="text-[10px] h-4 px-1">{attachments.length}</Badge>}
        </p>
        <Button variant="outline" size="sm" className="h-7 text-xs" disabled={uploading}
          onClick={() => fileRef.current?.click()} data-testid="upload-file-btn">
          {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
          Upload
        </Button>
        <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} data-testid="file-input" />
      </div>

      {attachments.length > 0 && (
        <div className="space-y-1.5">
          {attachments.map(att => {
            const Icon = FILE_ICONS[att.file_type] || File;
            return (
              <div key={att.file_id} className="flex items-center gap-2 py-1.5 px-2 bg-slate-50 rounded-lg group" data-testid={`attachment-${att.file_id}`}>
                <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-700 truncate">{att.original_name}</p>
                  <p className="text-[10px] text-slate-400">{formatSize(att.file_size)} - {att.uploaded_by_name}</p>
                </div>
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDownload(att.file_id, att.original_name)}>
                    <Download className="w-3 h-3 text-slate-400" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDelete(att.file_id)}>
                    <Trash2 className="w-3 h-3 text-slate-400 hover:text-red-500" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
